import { useId, useState } from "react";
import { Aviso, Boton, Modal } from "../../components/ui/primitivos";
import {
  destinosForzables,
  largoDelMotivo,
  MOTIVO_MAX,
  MOTIVO_MIN,
  motivoEsValido,
} from "./cambio-de-estado";
import { etiquetaDeEstado } from "./estados";

export type ModoDelDialogo = { tipo: "forzar" } | { tipo: "ordinario"; destino: string };

/**
 * El diálogo de un cambio de estado que deja motivo (ADR 0098 B14).
 *
 * Dos usos con el mismo cuerpo, para que la regla del motivo se vea igual en
 * las tres vistas:
 * - `forzar`: el salto forzado. Se elige el destino y se manda `forzar: true`.
 * - `ordinario`: un paso del flujo que exige motivo —hoy, cancelar desde
 *   `BORRADOR`—. El destino ya está decidido.
 *
 * Se monta abierto y se desmonta al cerrar: así cada apertura arranca vacía.
 * El error que se muestra es el del worker, tal cual (403, 409, 422).
 */
export function DialogoCambioDeEstado({
  numero,
  estadoActual,
  modo,
  onCerrar,
  alConfirmar,
}: {
  numero: string;
  estadoActual: string;
  modo: ModoDelDialogo;
  onCerrar: () => void;
  alConfirmar: (destino: string, motivo: string) => Promise<void>;
}) {
  const idDestino = useId();
  const idMotivo = useId();
  const idContador = useId();
  const [destino, setDestino] = useState(modo.tipo === "ordinario" ? modo.destino : "");
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  const forzar = modo.tipo === "forzar";
  const largo = largoDelMotivo(motivo);
  const listo = destino !== "" && motivoEsValido(motivo) && !trabajando;

  async function confirmar() {
    setTrabajando(true);
    setError(null);
    try {
      await alConfirmar(destino, motivo.trim());
      onCerrar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cambiar el estado");
      setTrabajando(false);
    }
  }

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={forzar ? `Forzar estado de ${numero}` : `Cancelar ${numero}`}
      descripcion={
        forzar
          ? `Desde ${etiquetaDeEstado(estadoActual)}, salteando el flujo y sus requisitos. Queda registrado con tu nombre y el motivo.`
          : `La OT está en ${etiquetaDeEstado(estadoActual)}. Cancelarla deja registrado el motivo.`
      }
    >
      <div className="space-y-4">
        {forzar ? (
          <div className="space-y-1">
            <label htmlFor={idDestino} className="block text-neutral-600 text-sm">
              Estado destino
            </label>
            <select
              id={idDestino}
              value={destino}
              onChange={(e) => setDestino(e.target.value)}
              className="w-full rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
            >
              <option value="">— elegí un estado —</option>
              {destinosForzables(estadoActual).map((e) => (
                <option key={e} value={e}>
                  {etiquetaDeEstado(e)}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="space-y-1">
          <label htmlFor={idMotivo} className="block text-neutral-600 text-sm">
            Motivo
          </label>
          <textarea
            id={idMotivo}
            rows={3}
            value={motivo}
            aria-describedby={idContador}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Por qué se hace este cambio…"
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm"
          />
          <p
            id={idContador}
            className={
              largo > MOTIVO_MAX || (largo > 0 && largo < MOTIVO_MIN)
                ? "text-red-700 text-xs"
                : "text-neutral-500 text-xs"
            }
          >
            {largo}/{MOTIVO_MAX} · mínimo {MOTIVO_MIN} caracteres
          </p>
        </div>

        {error ? <Aviso>{error}</Aviso> : null}

        <div className="flex justify-end gap-2">
          <Boton variante="secundario" onClick={onCerrar}>
            Volver
          </Boton>
          <Boton
            variante={forzar ? "primario" : "peligro"}
            disabled={!listo}
            onClick={() => void confirmar()}
          >
            {forzar ? "Forzar estado" : "Cancelar OT"}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}
